import { createTheme } from "@mui/material/styles";

import { brand } from "./brand.generated";
import { colors, fonts, palette, radius, shadows, typeScale } from "./tokens";

// Heading base (brand `type.heading`): 800, tight tracking, navy.
const heading = {
    fontWeight: brand.type.heading.weight,
    letterSpacing: brand.type.heading.tracking,
    color: colors.navy,
};
const scale = brand.type.scale;

// MUI v7 theme matching the Bison Commerce brand guide AND the public
// website's base layer (bisoncommerce-website/app/globals.css) — headings,
// links, buttons, focus and selection follow the site so a supplier moving
// from bisoncommerce.com into their room sees one brand (2026-09-03).
// Light theme only. Orange is accent-only — palette.primary is navy.
export const dealRoomTheme = createTheme({
    palette: {
        mode: "light",
        primary: {
            main: colors.navy,
            light: colors.navyLight,
            contrastText: colors.white,
        },
        secondary: {
            // Brand orange. Use sparingly — the primary CTA, the eyebrow, one
            // emphasis per view. Never a data series, never a left edge.
            main: colors.brandOrange,
            light: colors.orangeBright,
            contrastText: colors.white,
        },
        text: {
            primary: colors.navy,
            secondary: colors.darkGray,
        },
        // White ground (2026-10-26): on the website every section is a white
        // band over the #F7F7F7 body, so a visitor never sees the gray; the
        // room set its content straight on it and read gray. `colors.background`
        // stays for the PDF viewers' frame.
        background: {
            default: colors.white,
            paper: colors.white,
        },
        // 1px borders read `line`, the site's card/table border.
        divider: colors.line,
        grey: {
            50: "#FAFAFA",
            100: colors.lightGray,
            200: colors.lineSoft,
            300: colors.line,
            500: colors.mediumGray,
            700: colors.darkGray,
            900: colors.navy,
        },
    },
    typography: {
        fontFamily: fonts.body,
        // Site headings: 800, tight tracking, navy. Sizes and leading follow
        // the brand type scale (Bison-brand v1.0.0 `type.scale`): h1 is the
        // page title (36px, 48px from sm), h2 the section title (30px, 36px
        // from sm), h3 the column title (24px / 1.25), h4 the list title
        // (20px / 1.375).
        h1: {
            ...heading,
            fontSize: typeScale.pageTitle.fontSize.xs,
            lineHeight: typeScale.pageTitle.lineHeight,
            "@media (min-width:600px)": { fontSize: typeScale.pageTitle.fontSize.sm },
        },
        h2: {
            ...heading,
            fontSize: typeScale.sectionTitle.fontSize.xs,
            lineHeight: typeScale.sectionTitle.lineHeight,
            "@media (min-width:600px)": { fontSize: typeScale.sectionTitle.fontSize.sm },
        },
        h3: { ...heading, fontSize: scale["column-title"].size, lineHeight: Number(scale["column-title"].line) },
        h4: { ...heading, fontSize: scale["list-title"].size, lineHeight: Number(scale["list-title"].line) },
        h5: { fontWeight: 600, fontSize: "1.125rem", color: colors.navy },
        h6: { fontWeight: 600, fontSize: "1rem", color: colors.navy },
        subtitle1: { fontSize: "1rem", color: colors.darkGray },
        subtitle2: { fontSize: "0.875rem", color: colors.darkGray },
        // Brand body 16px / 1.625 and small 14px / 1.625.
        body1: { fontSize: scale.body.size, lineHeight: Number(scale.body.line), color: colors.darkGray },
        body2: { fontSize: scale.small.size, lineHeight: Number(scale.small.line), color: colors.darkGray },
        button: { fontWeight: 600, fontSize: "0.9375rem", textTransform: "none" },
        caption: { fontSize: "0.75rem", color: colors.mediumGray },
        // Site `.eyebrow`: 11px, 600, wide tracking, uppercase, orange.
        overline: {
            fontSize: scale.eyebrow.size,
            fontWeight: scale.eyebrow.weight,
            letterSpacing: scale.eyebrow.tracking,
            textTransform: scale.eyebrow.transform,
            lineHeight: 1.6,
            color: colors.brandOrange,
        },
    },
    shape: { borderRadius: radius.md },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    WebkitFontSmoothing: "antialiased",
                    textRendering: "optimizeLegibility",
                },
                "::selection": { background: brand.state.selection },
                ":focus-visible": {
                    outline: `${brand.state["focus-ring"].width} solid ${brand.state["focus-ring"].color}`,
                    outlineOffset: brand.state["focus-ring"].offset,
                    borderRadius: brand.state["focus-ring"].radius,
                },
                "input:focus-visible, textarea:focus-visible, select:focus-visible": {
                    outline: "none",
                },
            },
        },
        MuiButton: {
            defaultProps: { disableElevation: true },
            styleOverrides: {
                // Site `.btn`: 600, 15px, 14/24 padding, radius md, lift on hover.
                root: {
                    borderRadius: radius.md,
                    padding: "12px 20px",
                    lineHeight: 1.2,
                    transition:
                        "transform 150ms ease, background-color 150ms ease, border-color 150ms ease, color 150ms ease, box-shadow 150ms ease",
                },
                sizeSmall: { padding: "8px 14px", fontSize: "0.875rem" },
                // `.btn-primary`: Bison blue, white text, shadow-md, deeper + lifted on
                // hover. Was brand orange until 2026-09-06 (Bill): red-orange reads as
                // a warning, so a primary action is blue and orange stays for the
                // eyebrow and one emphasis per view.
                containedSecondary: {
                    backgroundColor: palette.blue,
                    boxShadow: shadows.md,
                    "&:hover": {
                        backgroundColor: palette.blueDark,
                        boxShadow: shadows.md,
                        transform: "translateY(-1px)",
                    },
                },
                // `.btn-secondary`: transparent, navy text, line border; navy border + white on hover.
                outlined: {
                    color: colors.navy,
                    borderColor: colors.line,
                    backgroundColor: "transparent",
                    "&:hover": {
                        borderColor: colors.navy,
                        backgroundColor: colors.white,
                    },
                },
                outlinedSecondary: {
                    color: colors.navy,
                    borderColor: colors.line,
                    "&:hover": { borderColor: colors.navy, backgroundColor: colors.white },
                },
            },
        },
        MuiPaper: {
            defaultProps: { elevation: 0 },
            styleOverrides: {
                root: { backgroundImage: "none", borderRadius: radius.lg },
            },
        },
        // Chips are pills: the one exception to the 10px radius cap
        // (Bison-brand v1.0.0, "Chips and pagination", 2026-10-25).
        MuiChip: {
            styleOverrides: { root: { borderRadius: radius.full } },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: radius.md,
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.line },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: brand.state["field-focus"].border,
                        borderWidth: 1,
                    },
                },
            },
        },
        MuiAppBar: {
            defaultProps: { elevation: 0, color: "primary" },
            // AppBar is a Paper, so it would inherit the Paper radius above;
            // the mobile top bar is edge-to-edge and must have square corners.
            styleOverrides: { root: { borderRadius: 0 } },
        },
        MuiDrawer: {
            // The drawer paper is a Paper too: the phone nav sheet showed the
            // 10px radius on its right corners (Bill, 2026-09-09).
            styleOverrides: { paper: { borderRadius: 0 } },
        },
        MuiLink: {
            // Site inline link: semibold navy, always underlined, orange 2px
            // underline offset 2px. Orange stays the decoration, not the text.
            defaultProps: { underline: "always" },
            styleOverrides: {
                root: {
                    color: colors.navy,
                    fontWeight: 600,
                    textDecorationColor: colors.brandOrange,
                    textDecorationThickness: 2,
                    textUnderlineOffset: 2,
                    "&:hover": { color: colors.navyLight },
                },
            },
        },
    },
});
